import React from "react";
import { Check, ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";
import { C, RARITY } from "../../constants/design";
import { SLOT, SLOT_DEFS, STATS, STAT_DISPLAY_ORDER, INVENTORY_IMGS } from "../../constants/items";
import { StatIcon } from "./StatIcon";
import { getStats, iName, iSkin } from "../../engine/itemEngine";
import type { Item } from "../../types/game";

type SlotDef = { emoji: string; iconImg?: string };

/**
 * Green upgrade arrow (same shape as the one shown in pack reveals), rendered
 * small and static — no animation. Signals an inventory item that is stronger
 * than what's currently equipped in its slot.
 */
export function UpgradeArrow({ size = 14 }: { size?: number }) {
  const height = Math.round((size * 25) / 20); // keep the 20×25 viewBox ratio
  return (
    <svg width={size} height={height} viewBox="0 0 20 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.22168 0.31543C9.61668 -0.105146 10.2848 -0.105238 10.6797 0.31543L19.6289 9.85449C20.2277 10.4931 19.7749 11.5381 18.8994 11.5381H14.3252L15.3096 19.4141C15.3842 20.0109 14.9189 20.5381 14.3174 20.5381H5.58301C4.98166 20.5379 4.51623 20.0108 4.59082 19.4141L5.5752 11.5381H1.00195C0.12637 11.5381 -0.326543 10.4931 0.272461 9.85449L9.22168 0.31543Z"
        fill="#00FF55"
      />
    </svg>
  );
}

function SlotIcon({ slotDef, size, style }: { slotDef: SlotDef; size: number; style?: React.CSSProperties }) {
  if (slotDef.iconImg) {
    return <img src={slotDef.iconImg} alt="" style={{ width: size, height: size, objectFit: "contain", ...style }} />;
  }
  return <span style={{ fontSize: size, ...style }}>{slotDef.emoji}</span>;
}

export function ItemCardContent({
  it,
  r,
  slotDef,
  statEntries,
}: {
  it: Item;
  r: (typeof RARITY)[keyof typeof RARITY];
  slotDef: SlotDef;
  statEntries: [string, number][];
}) {
  const { t } = useI18n();
  const isLeg = it.rarity === "ultra" || it.rarity === "epic";

  return (
    <>
      <span
        style={{
          fontSize: 8.5,
          fontWeight: 900,
          letterSpacing: 0.7,
          textTransform: "uppercase",
          color: "#000000",
          background: r.color,
          borderRadius: 999,
          padding: "3px 9px",
          lineHeight: 1.5,
          zIndex: 2,
        }}
      >
        {r.label}
      </span>

      <div
        style={{
          position: "absolute",
          top: "40px",
          width: "70px",
          height: "70px",
          borderRadius: "50%",
          backgroundColor: r.color,
          filter: "blur(20px)",
          opacity: 0.35,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          width: "100%",
        }}
      >
        {INVENTORY_IMGS[iSkin(it)] ? (
          <img
            src={INVENTORY_IMGS[iSkin(it)]}
            alt=""
            style={{
              width: 64,
              height: 64,
              objectFit: "contain",
              filter: `drop-shadow(0 4px 12px ${r.glow})`,
            }}
          />
        ) : (
          <SlotIcon slotDef={slotDef} size={38} style={{ filter: `drop-shadow(0 4px 12px ${r.glow})`, animation: isLeg ? "capPulse 1.6s infinite" : "none" }} />
        )}

        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.text,
            textAlign: "center",
            lineHeight: 1.25,
            minHeight: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {iSkin(it)}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          width: "100%",
          zIndex: 2,
          marginTop: "auto",
        }}
      >
        {statEntries.map(([key, val]) => {
          const sd = STATS.find((s) => s.key === key);
          const isBlessure = key === "blessure";
          const isEnergy = key === "energy";
          const iconColor = isBlessure ? "#e06a8c" : isEnergy ? "#F5B301" : sd?.color || C.faint;
          const fullName = isBlessure
            ? t("itemModal.defense")
            : t(`items.stats.${key}`);
          return (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "3px 0",
              }}
            >
              {isBlessure
                ? <ShieldCheck size={11} color={iconColor} />
                : isEnergy
                ? <span style={{ fontSize: 11, lineHeight: 1 }}>💧</span>
                : sd && <StatIcon st={sd} size={11} />
              }
              <span style={{ fontSize: 9.5, fontWeight: 900, color: C.turf }}>
                +{val} {fullName}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function ItemCard({
  it,
  equipped,
  onClick,
}: {
  it: Item;
  equipped?: boolean;
  onClick?: () => void;
}) {
  const r = RARITY[it.rarity];
  const slotDef = SLOT[it.slot] || { emoji: "?" };
  const itemStats = getStats(it);
  const statEntries = (Object.entries(itemStats) as [string, number][])
    .sort(([a], [b]) => STAT_DISPLAY_ORDER.indexOf(a) - STAT_DISPLAY_ORDER.indexOf(b));
  const isLeg = it.rarity === "ultra";

  return (
    <button
      onClick={onClick}
      style={{
        background: `linear-gradient(165deg,${r.color}12,${C.surface})`,
        border: `2px solid ${equipped ? C.turf : r.color}`,
        borderRadius: 16,
        padding: "14px 11px 11px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 7,
        position: "relative",
        width: "100%",
        boxShadow: equipped
          ? `0 0 0 2.5px ${C.turf},0 8px 28px ${r.glow}`
          : `0 4px 22px ${r.glow}`,
        transition: "transform .12s, box-shadow .12s",
      }}
    >
      {equipped && (
        <span
          style={{
            position: "absolute",
            top: 7,
            right: 7,
            background: C.turf,
            borderRadius: "50%",
            width: 19,
            height: 19,
            display: "grid",
            placeItems: "center",
            zIndex: 10,
          }}
        >
          <Check size={11} color="#062012" />
        </span>
      )}

      {isLeg && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 16,
            background: `radial-gradient(circle at 50% 30%,${r.color}18,transparent 70%)`,
            animation: "capPulse 2.2s infinite",
            color: r.color,
          }}
        />
      )}

      <ItemCardContent
        it={it}
        r={r}
        slotDef={slotDef}
        statEntries={statEntries}
      />
    </button>
  );
}

export function ItemToken({
  it,
  isUpgrade,
  onClick,
}: {
  it: Item;
  isUpgrade?: boolean;
  onClick?: () => void;
}) {
  const r = RARITY[it.rarity];
  const slotDef = SLOT[it.slot] || { emoji: "?" };
  const isLeg = it.rarity === "ultra" || it.rarity === "epic";

  return (
    <button
      onClick={onClick}
      style={{
        aspectRatio: "1",
        borderRadius: 12,
        border: `2px solid ${r.color}`,
        background: `linear-gradient(165deg,${r.color}15,${C.surface})`,
        boxShadow: `0 4px 18px ${r.glow}`,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        padding: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 50%,${r.color}22,transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      {isLeg && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 50% 30%,${r.color}18,transparent 70%)`,
            animation: "capPulse 2.2s infinite",
          }}
        />
      )}

      {isUpgrade && (
        <span
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 3,
            display: "grid",
            placeItems: "center",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
            pointerEvents: "none",
          }}
        >
          <UpgradeArrow size={12} />
        </span>
      )}

      {it.cosmetic && (
        <span
          style={{
            position: "absolute",
            top: 2,
            left: 3,
            fontSize: 9,
            zIndex: 4,
            lineHeight: 1,
            pointerEvents: "none",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))",
          }}
        >
          ✨
        </span>
      )}

      {INVENTORY_IMGS[iSkin(it)] ? (
        <img
          src={INVENTORY_IMGS[iSkin(it)]}
          alt=""
          style={{
            width: "80%",
            height: "80%",
            objectFit: "contain",
            position: "relative",
            zIndex: 2,
          }}
        />
      ) : (
        <SlotIcon slotDef={slotDef} size={24} style={{ filter: `drop-shadow(0 3px 10px ${r.glow})`, position: "relative", zIndex: 2 }} />
      )}
    </button>
  );
}

export function SlotToken({
  slotDef,
  equippedItem,
  onClick,
}: {
  slotDef: SlotDef & { key: string; label: string };
  equippedItem: Item | null;
  onClick?: () => void;
}) {
  const { t } = useI18n();
  if (equippedItem) {
    return <ItemToken it={equippedItem} onClick={onClick} />;
  }
  return (
    <div
      style={{
        aspectRatio: "1",
        borderRadius: 12,
        border: `1.5px dashed ${C.line}`,
        background: C.surface,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
      }}
    >
      <SlotIcon slotDef={slotDef} size={18} style={{ opacity: 0.25 }} />
      <span
        style={{
          fontSize: 7.5,
          color: C.faint,
          fontWeight: 700,
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {t(`items.slots.${slotDef.key}`)}
      </span>
    </div>
  );
}
